/**
 * ledgerMomentOfValue
 *
 * Turns a cell or record value into a Luxon `DateTime` set to the tenant zone, or `null`. A
 * Firestore Timestamp (duck-typed through `isTimestampShaped`, since client and admin SDKs differ)
 * keeps its instant. A string is ISO-8601: one carrying an offset keeps its instant, and a date-only
 * or offset-less one is read as tenant-local. A string must carry a date, so the result never
 * depends on the clock: one Luxon could also read as a bare time of day (`'12'`, `'12:00'`, and the
 * day-less `'2026'` and `'2026-03'`) is refused, because a bare time takes today's date. Anything
 * else, or an invalid result, is `null`. Consumers: `evaluateLedgerAccrualBinding`'s `triggerDate`
 * role, `ledgerPeriodAt`'s anchor and `validateLedgerDefinition`'s moments.
 */

import { DateTime } from 'luxon';
import { isTimestampShaped } from '../formDeriveEngine/isTimestampShaped';

function readsAsTimeOfDay(text: string): boolean {
    return DateTime.fromISO(`2000-01-01T${text}`).isValid;
}

function ledgerMomentOfValue(value: unknown, timeZone: string): DateTime | null {
    const moment = isTimestampShaped(value)
        ? DateTime.fromMillis(value.seconds * 1000 + Math.floor(value.nanoseconds / 1e6), { zone: timeZone })
        : typeof value === 'string' && !readsAsTimeOfDay(value)
            ? DateTime.fromISO(value, { zone: timeZone })
            : null;
    return moment !== null && moment.isValid ? moment : null;
}

export { ledgerMomentOfValue };
